/**
 * DateChangeReasonDialog — mobile-friendly styled modal that replaces the
 * old `window.prompt` for the "why is this task date changing?"
 * justification. Resolves with { reason, cancelled }.
 *
 * Usage: `const reason = await askDateChangeReason(fields);`
 *   → returns `null` if the user cancelled, else the trimmed string.
 *
 * Feb 2026 R293j · Spawned to kill the native-prompt UX inconsistency
 * reported on mobile; the browser's chrome dialog felt out of place in
 * the dense dark tasks UI and shipped tiny unreadable text on narrow
 * viewports.
 */
import { useState, useRef } from "react";
import { createRoot } from "react-dom/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CalendarClock, X } from "lucide-react";

function DateChangeReasonDialog({ fields, onResolve }) {
  const [open, setOpen] = useState(true);
  const [reason, setReason] = useState("");
  const inputRef = useRef(null);
  const prettyFields = (fields || [])
    .map((k) => k.replace(/_date$/, "").replace(/_/g, " "))
    .join(" / ") || "date";

  const close = (val) => {
    setOpen(false);
    // Give Radix a tick to run its exit animation before unmounting.
    setTimeout(() => onResolve(val), 180);
  };

  const submit = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      inputRef.current?.focus();
      return;
    }
    close(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(null); }}>
      <DialogContent className="rounded-none max-w-md" data-testid="date-change-reason-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="w-4 h-4 text-primary" />
            Why is the {prettyFields} changing?
          </DialogTitle>
          <DialogDescription className="text-xs">
            This note is logged to the task's activity trail so collaborators understand the shift.
          </DialogDescription>
        </DialogHeader>
        <div className="pt-2">
          <Textarea
            ref={inputRef}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Client approval delayed by 2 days, awaiting assets, etc."
            rows={4}
            autoFocus
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            className="rounded-none text-sm"
            data-testid="date-change-reason-input"
          />
          <p className="text-[10px] text-muted-foreground mt-1.5">
            Tip: ⌘/Ctrl + Enter to confirm.
          </p>
        </div>
        <DialogFooter className="flex flex-row justify-end gap-2 pt-3">
          <Button type="button" variant="ghost" size="sm"
                   onClick={() => close(null)}
                   className="rounded-none"
                   data-testid="date-change-reason-cancel">
            <X className="w-3.5 h-3.5 mr-1" /> Cancel
          </Button>
          <Button type="button" size="sm"
                   onClick={submit}
                   disabled={!reason.trim()}
                   className="rounded-none"
                   data-testid="date-change-reason-submit">
            Save reason & update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Imperative API: show the dialog, resolve with the reason string
 *  (never empty) or null when the user cancels. */
export function askDateChangeReason(fields) {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    host.setAttribute("data-portal", "date-change-reason");
    document.body.appendChild(host);
    const root = createRoot(host);
    const cleanup = (val) => {
      root.unmount();
      try { document.body.removeChild(host); } catch { /* noop */ }
      resolve(val);
    };
    root.render(<DateChangeReasonDialog fields={fields} onResolve={cleanup} />);
  });
}

export default askDateChangeReason;
